/**
 * StatusBadge: every state enum in @windrop/contracts mapped to a token tone AND text (§13.3: colour is never
 * the only carrier of meaning). Tones: neutral · info · success · warning · error.
 */
import {
  ClaimStatus,
  DrawStatus,
  EntryStatus,
  FulfillmentStatus,
  HoldStatus,
  LotteryStatus,
  PaymentStatus,
  SupportCaseStatus,
  TaskCompletionStatus,
  VerificationDecision,
  WinStatus,
  WinnerContactStatus,
} from '@windrop/contracts';

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'error';

const map = <T extends string>(values: readonly T[], tones: Partial<Record<T, Tone>>): Record<T, Tone> => {
  const out = {} as Record<T, Tone>;
  for (const v of values) out[v] = tones[v] ?? 'neutral';
  return out;
};

export const STATUS_TONES = {
  LotteryStatus: map(LotteryStatus.options, {
    DRAFT: 'neutral',
    REVIEW: 'info',
    APPROVED: 'info',
    SCHEDULED: 'info',
    LIVE: 'success',
    PAUSED: 'warning',
    CLOSED: 'neutral',
    DRAWN: 'success',
    SETTLED: 'success',
    CANCELLED: 'error',
  }),
  EntryStatus: map(EntryStatus.options, {
    PENDING: 'warning',
    ELIGIBLE: 'success',
    SNAPSHOT_INCLUDED: 'success',
    INELIGIBLE: 'error',
    REFUNDED: 'neutral',
    DISQUALIFIED: 'error',
  }),
  PaymentStatus: map(PaymentStatus.options, {
    INITIALIZED: 'neutral',
    PENDING: 'warning',
    SUCCEEDED: 'success',
    FAILED: 'error',
    EXPIRED: 'neutral',
    CANCELLED: 'neutral',
    REFUNDED: 'info',
    PARTIALLY_REFUNDED: 'info',
    RECONCILIATION_EXCEPTION: 'error',
  }),
  DrawStatus: map(DrawStatus.options, {
    COMMITTED: 'info',
    SNAPSHOT_LOCKED: 'info',
    ALLOCATED: 'info',
    PUBLISHED: 'success',
    REVEALED: 'success',
    VERIFIED: 'success',
    FAILED: 'error',
    CANCELLED: 'error',
  }),
  WinStatus: map(WinStatus.options, {
    WON: 'success',
    CLAIM_OPENED: 'info',
    CLAIM_SUBMITTED: 'info',
    APPROVED: 'success',
    FULFILLED: 'success',
    EXPIRED: 'neutral',
    FORFEITED: 'warning',
    REDRAWN: 'warning',
  }),
  HoldStatus: map(HoldStatus.options, {
    ACTIVE: 'info',
    CONVERTED: 'success',
    EXPIRED: 'neutral',
    RELEASED: 'neutral',
  }),
  ClaimStatus: map(ClaimStatus.options, {
    OPENED: 'info',
    VARIANT_HELD: 'info',
    SUBMITTED: 'info',
    VERIFICATION_PENDING: 'warning',
    VERIFIED: 'success',
    MORE_INFO: 'warning',
    REJECTED: 'error',
    APPROVED: 'success',
    IN_FULFILLMENT: 'info',
    PAID: 'success',
    DELIVERED: 'success',
    EXPIRED: 'neutral',
    FORFEITED: 'warning',
    REDRAWN: 'warning',
  }),
  VerificationDecision: map(VerificationDecision.options, {
    VERIFIED: 'success',
    REJECTED: 'error',
    MORE_INFO: 'warning',
    PENDING: 'neutral',
  }),
  WinnerContactStatus: map(WinnerContactStatus.options, {
    NOT_MESSAGED: 'warning',
    BUTTON_TAPPED: 'info',
    MESSAGED: 'success',
    FOLLOWED_UP: 'success',
  }),
  FulfillmentStatus: map(FulfillmentStatus.options, {
    NOT_READY: 'neutral',
    READY: 'info',
    IN_PROGRESS: 'info',
    EXCEPTION: 'error',
    COMPLETED: 'success',
  }),
  SupportCaseStatus: map(SupportCaseStatus.options, {
    OPEN: 'warning',
    ASSIGNED: 'info',
    WAITING_PARTICIPANT: 'neutral',
    WAITING_PLATFORM: 'warning',
    RESOLVED: 'success',
    CLOSED: 'neutral',
  }),
  TaskCompletionStatus: map(TaskCompletionStatus.options, {
    PENDING: 'warning',
    QUALIFIED: 'success',
    REJECTED: 'error',
  }),
} as const;

export type StatusKind = keyof typeof STATUS_TONES;
export type StatusValue<K extends StatusKind> = keyof (typeof STATUS_TONES)[K] & string;

/** `SNAPSHOT_INCLUDED` → `Snapshot included`. */
export const humanizeStatus = (value: string): string => {
  const words = value.toLowerCase().split('_');
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
};

export interface StatusBadgeProps<K extends StatusKind> {
  kind: K;
  value: StatusValue<K>;
  /** Override the visible text (e.g. localized); the enum value stays in `data-value`. */
  label?: string;
  size?: 'sm' | 'md';
}

export function StatusBadge<K extends StatusKind>({ kind, value, label, size = 'md' }: StatusBadgeProps<K>) {
  const tone = (STATUS_TONES[kind] as Record<string, Tone>)[value] ?? 'neutral';
  return (
    <span className="wdc-badge" data-tone={tone} data-kind={kind} data-value={value} data-size={size}>
      <span className="wdc-badge__dot" aria-hidden="true" />
      {label ?? humanizeStatus(value)}
    </span>
  );
}

/** A plain tone badge for non-enum signals (SLA, severity). */
export function ToneBadge({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className="wdc-badge" data-tone={tone}>
      <span className="wdc-badge__dot" aria-hidden="true" />
      {children}
    </span>
  );
}
